// Quill 2 용 이미지 리사이즈 모듈 (의존성 없음)
// - 이미지를 클릭하면 4개 코너에 핸들 표시
// - 어느 코너든 드래그하여 크기 조절
// - Shift 를 누른 채 드래그하면 원본 비율로 고정
//
// 크기는 <img> 의 width/height "속성"으로 저장한다.
// Quill 2 의 Image blot 이 width/height 를 기본 포맷으로 인식하므로
// 변경이 Delta 에 반영되고(onChange 발생) 저장/재편집 시에도 유지된다.

const MIN_SIZE = 30;
const HANDLE_POSITIONS = ["nw", "ne", "sw", "se"];

export default class ImageResize {
  constructor(quill) {
    this.quill = quill;
    this.img = null;
    this.overlay = null;
    this.drag = null;

    this.handleClick = this.handleClick.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.reposition = this.reposition.bind(this);
    this.hide = this.hide.bind(this);

    this.quill.root.addEventListener("click", this.handleClick);
    this.quill.root.addEventListener("scroll", this.reposition);
    this.quill.on("text-change", this.reposition);
    window.addEventListener("resize", this.reposition);
  }

  handleClick(e) {
    const target = e.target;
    // 핸들 자체를 클릭한 경우는 무시 (드래그 종료 후 hide 방지)
    if (this.overlay && this.overlay.contains(target)) return;
    if (target && target.tagName === "IMG") {
      this.show(target);
    } else {
      this.hide();
    }
  }

  show(img) {
    if (this.img === img && this.overlay) return;
    this.hide();
    this.img = img;

    const container = this.quill.root.parentNode; // .ql-container
    if (getComputedStyle(container).position === "static") {
      container.style.position = "relative";
    }

    const overlay = document.createElement("div");
    overlay.className = "ql-img-resizer";
    HANDLE_POSITIONS.forEach((pos) => {
      const handle = document.createElement("span");
      handle.className = `ql-img-resizer__handle ql-img-resizer__handle--${pos}`;
      handle.addEventListener("mousedown", (ev) => this.startDrag(ev, pos));
      overlay.appendChild(handle);
    });
    container.appendChild(overlay);
    this.overlay = overlay;
    this.reposition();
  }

  reposition() {
    if (!this.img || !this.overlay) return;
    if (!this.img.isConnected) {
      this.hide();
      return;
    }
    const container = this.quill.root.parentNode;
    const imgRect = this.img.getBoundingClientRect();
    const contRect = container.getBoundingClientRect();
    this.overlay.style.left = `${imgRect.left - contRect.left + container.scrollLeft}px`;
    this.overlay.style.top = `${imgRect.top - contRect.top + container.scrollTop}px`;
    this.overlay.style.width = `${imgRect.width}px`;
    this.overlay.style.height = `${imgRect.height}px`;
  }

  startDrag(e, pos) {
    if (!this.img) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = this.img.getBoundingClientRect();
    this.drag = {
      pos,
      startX: e.clientX,
      startY: e.clientY,
      width: rect.width,
      height: rect.height,
      ratio: rect.height ? rect.width / rect.height : 1,
    };
    document.addEventListener("mousemove", this.handleMouseMove);
    document.addEventListener("mouseup", this.handleMouseUp);
  }

  handleMouseMove(e) {
    if (!this.drag || !this.img) return;
    const { pos, startX, startY, width, height } = this.drag;
    const xDir = pos.includes("e") ? 1 : -1; // 동쪽 핸들은 오른쪽으로 끌면 커짐
    const yDir = pos.includes("s") ? 1 : -1; // 남쪽 핸들은 아래로 끌면 커짐
    const newWidth = Math.max(
      MIN_SIZE,
      Math.round(width + (e.clientX - startX) * xDir),
    );

    if (e.shiftKey) {
      // 비율 고정: width 만 지정하면 <img> 가 원본 비율로 높이를 맞춘다
      this.img.setAttribute("width", newWidth);
      this.img.removeAttribute("height");
    } else {
      const newHeight = Math.max(
        MIN_SIZE,
        Math.round(height + (e.clientY - startY) * yDir),
      );
      this.img.setAttribute("width", newWidth);
      this.img.setAttribute("height", newHeight);
    }
    this.reposition();
  }

  handleMouseUp() {
    document.removeEventListener("mousemove", this.handleMouseMove);
    document.removeEventListener("mouseup", this.handleMouseUp);
    this.drag = null;
    // DOM 변경을 Quill 모델에 반영시켜 onChange(저장)가 발생하도록 한다
    this.quill.update("user");
    this.reposition();
  }

  hide() {
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
    this.img = null;
  }
}
